/**
 * Registry of in-process caches that hold Emma data, and the shared
 * "cache generation" that tells every container to drop them.
 *
 * Each cache registers a clear function. A bust (pages/api/cache/bust.js) clears the
 * caches in the container that received it, then writes a new generation to the
 * snapshot store; every other container polls that generation and clears its own
 * caches when it changes. See docs/cache-busting.md.
 *
 * The polling lives in generationWatch.js. This module must stay free of Node-only imports:
 * services/emma/server.js imports it and also ends up in browser bundles.
 *
 * State lives on globalThis: Next can bundle this module more than once (API routes,
 * instrumentation), and the dev server re-evaluates it on HMR. Registering under a
 * name replaces the previous registration rather than adding another.
 */

const state = (globalThis.__cacheRegistry ??= {
    clearers: new Map(), // name -> clear({ meetingId })
    generation: null, // last generation this container applied
    bustedAt: 0, // when this container last cleared for a bust
})

/** `clear` receives `{ meetingId }` (null = everything) and must not throw. */
export function registerCache(name, clear) {
    state.clearers.set(name, clear)
}

/** When this container last cleared for a bust (0 if never). */
export function lastBustAt() {
    return state.bustedAt
}

export function currentGeneration() {
    return state.generation
}

/** Clears every registered cache in this container and records the generation as applied. */
export function clearLocalCaches({ meetingId = null, generation = null } = {}) {
    state.bustedAt = Date.now()
    if (generation) state.generation = generation
    const cleared = []
    for (const [name, clear] of state.clearers) {
        try {
            clear({ meetingId })
            cleared.push(name)
        } catch (err) {
            console.error(`[cache] clearing ${name} failed: ${err.message}`)
        }
    }
    console.log(`[cache] cleared ${cleared.join(", ") || "nothing"} (meeting=${meetingId ?? "all"}, generation=${generation ?? "local"})`)
    return cleared
}

/** Records the generation this container has applied, without clearing anything. */
export function setGeneration(generation) {
    state.generation = generation
}
